import React from "react";

import { GetStaticProps } from "next";
import {
  BTypeBeneficiaryHead,
  CTypeCardView,
  DTypeBreadCrumb,
  colors,
  FullWidthButton,
  nationalMeritFaqRichSearch,
  RichSearch,
  ATypeNationalMeritHead,
  FullWidthTwinButton,
  call,
  ATypeNationalMeritSummary,
  GOI_WEB_URL,
  customEvent,
} from "goi_common";
import crematorium from "goi_common/src/constants/hwajang_detail.json";
import facilities from "goi_common/src/constants/facilities_detail.json";
import parlors from "goi_common/src/constants/parlors_detail.json";
import { parlorRegion } from "@/constants/parlorRegion";
import { Box, Stack } from "@mui/material";
import Head from "next/head";

const { regionColor, targetSidos, bestParlorIds } = parlorRegion;

export const getStaticProps: GetStaticProps = async () => {
  const regionCrematoriums = crematorium.filter((p) =>
    targetSidos.includes(String(p.sido))
  );
  const regionPublicFacilities = facilities.filter(
    (f) => targetSidos.includes(f.sido) && f.is_public
  );
  const regionBestParlors = parlors.filter((p) => bestParlorIds.includes(p.id));
  const description =
    "수도권 국가유공자 장례 혜택 1) 장례식장 별 혜택 제공 2)지역 내 화장시설 화장 비용 무료 3) 지역 내 공설 장지 우선 안치 4) 국립현충원 및 호국원 안장";

  return {
    props: {
      regionCrematoriums,
      regionPublicFacilities,
      regionBestParlors,
      pageDescription: description,
      pageKeywords: `국가유공자 장례, 수도권 국가유공자 장례, 서울경기인천 국가유공자 장례, 서울경기인천 국가유공자 장례 준비, 서울경기인천 국가유공자 장례 비용`,
      pageOgTitle: `국가유공자 장례 114 
      (서울경기인천) | 서울경기인천장례정보포털`,
      pageOgDescription: description,
      pageOgImage: "",
    },
  };
};

export default function index({
  regionCrematoriums,
  regionPublicFacilities,
  regionBestParlors,
  pageOgTitle,
}: {
  regionCrematoriums: Array<any>;
  regionPublicFacilities: Array<any>;
  regionBestParlors: Array<any>;
  pageOgTitle: string;
}) {
  const faqList = nationalMeritFaqRichSearch;
  return (
    <>
      <Head>
        <title>{pageOgTitle}</title>
        <RichSearch data={faqList} />
      </Head>
      <DTypeBreadCrumb
        mainColor={regionColor}
        firstItem={{
          text: `수도권 국가유공자 장례114`,
          href: `/veterans/`,
          event: {
            domain: "metro",
            path: "veterans",
            trigger_and_target: "br_crumb_veterans",
          },
        }}
        goiHomeEvent={{
          domain: "metro",
          path: "veterans",
          trigger_and_target: "br_crumb_goi_home",
        }}
      />

      <ATypeNationalMeritHead
        titleSido="서울 · 경기 · 인천"
        mainColor={regionColor}
        title="국가유공자 장례114"
      />

      <Stack padding="20px" gap="12px">
        <Box className="subtitle_14_b" textAlign="center">
          상담을 통해 자세한 국가 유공자 장례 혜택을 안내드립니다.
        </Box>
        <FullWidthTwinButton
          firstBtn={{
            isGoiCall: true,
            text: "상담하기",
            onClick: () => {
              call("1666-9784");
              customEvent({
                domain: "metro",
                path: "veterans",
                trigger_and_target: "goi_call",
              });
            },
          }}
          secondBtn={{
            text: "비용 알아보기",
            href: `${GOI_WEB_URL}?utm_source=dogsound` + "/match/request/?utm_source=dogsound",
            onClick: () => {
              customEvent({
                domain: "metro",
                path: "veterans",
                trigger_and_target: "uv_mth_req",
              });
            },
          }}
          backgroundColor={regionColor.c400}
        />
        <ATypeNationalMeritSummary mainColor={regionColor} />
      </Stack>

      <Box padding="0 20px">
        <Box
          width="100%"
          height="1px"
          style={{ background: colors.gray900 }}
          margin="0 0 20px"
          padding="0 20px"
        />
      </Box>

      <Stack margin="20px 12px" padding="24px 16px 10px" gap="12px">
        <h3 className="subtitle_18_b" style={{ textAlign: "center" }}>
          수도권 지역 장례식장
        </h3>
        {regionBestParlors.map((res) => (
          <CTypeCardView
            key={res.companyname}
            name={res.companyname}
            mainColor={regionColor}
            sido={res.sido}
            sigungu={res.sigungu}
            href={`https://www.goifuneral.co.kr/match/parlor-request/${res.companyname}/`}
            imgSrc={res.images[0] ? res.images[0].url : ""}
            arrowText="혜택 알아보기"
            event={{
              domain: "metro",
              path: "veterans",
              trigger_and_target: "uv_prlr_detail",
            }}
          />
        ))}
      </Stack>

      <Stack padding="40px 20px" marginBottom="40px" gap="8px">
        <h3 className="subtitle_18_b" style={{ textAlign: "center" }}>
          수도권 지역 화장장
        </h3>
        {regionCrematoriums.map((res) => (
          <CTypeCardView
            sigungu={res.sigungu}
            mainColor={regionColor}
            key={res.companyname}
            name={res.companyname}
            href={`/veterans/hwajang/${res.companyname}/`}
            descriptions={["국가유공자 무료"]}
            sido={res.sido}
            imgSrc={res.images[0] ? res.images[0].url : ""}
            event={{
              domain: "metro",
              path: "veterans",
              trigger_and_target: "uv_crema_detail",
            }}
          />
        ))}
      </Stack>

      <Stack padding="40px 20px" marginBottom="40px" gap="8px">
        <h3 className="subtitle_18_b" style={{ textAlign: "center" }}>
          수도권 지역 장지 (공설)
        </h3>
        {regionPublicFacilities.map((res) => (
          <CTypeCardView
            sigungu={res.sigungu}
            mainColor={regionColor}
            key={res.companyname}
            name={res.companyname}
            href={`/veterans/jangji/${res.companyname}/`}
            sido={res.sido}
            descriptions={["국가유공자 우선 안치"]}
            imgSrc={res.images[0] ? res.images[0].url : ""}
            event={{
              domain: "metro",
              path: "veterans",
              trigger_and_target: "uv_facility_detail",
            }}
          />
        ))}
      </Stack>
    </>
  );
}
